import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Divider,
  Tab,
  Tabs,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Build as BuildIcon,
  Assignment as AssignmentIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Description as DescriptionIcon,
  Download as DownloadIcon,
  Timeline as TimelineIcon,
  DirectionsCar as CarIcon,
  Schedule as ScheduleIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { DashboardLayout } from '../../components/layout/DashboardLayout';

interface ServiceRecord {
  id: string;
  date: Date;
  type: 'maintenance' | 'repair' | 'inspection' | 'upgrade';
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  vehicle: {
    make: string;
    model: string;
    year: number;
    vin: string;
    registration: string;
  };
  description: string;
  technician: string;
  cost: number;
  parts: {
    partNumber: string;
    name: string;
    quantity: number;
    unitPrice: number;
  }[];
  documents: {
    type: string;
    url: string;
  }[];
  notes: string;
  mileage: number;
  nextService: Date;
}

interface FilterForm {
  type: string[];
  status: string[];
  dateRange: {
    start: string;
    end: string;
  };
  technician: string;
}

interface NewServiceForm {
  type: 'maintenance' | 'repair' | 'inspection' | 'upgrade';
  vehicle: {
    make: string;
    model: string;
    year: number;
    vin: string;
    registration: string;
  };
  description: string;
  technician: string;
  mileage: number;
  parts: {
    partNumber: string;
    name: string;
    quantity: number;
    unitPrice: number;
  }[];
  notes: string;
}

export const ServiceHistory: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<ServiceRecord | null>(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const [openNewServiceDialog, setOpenNewServiceDialog] = useState(false);
  const [filters, setFilters] = useState<FilterForm>({
    type: [],
    status: [],
    dateRange: {
      start: '',
      end: '',
    },
    technician: '',
  });
  const [newService, setNewService] = useState<NewServiceForm>({
    type: 'maintenance',
    vehicle: {
      make: '',
      model: '',
      year: new Date().getFullYear(),
      vin: '',
      registration: '',
    },
    description: '',
    technician: '',
    mileage: 0,
    parts: [],
    notes: '',
  });

  // Mock service history data for luxury vehicles
  const serviceRecords: ServiceRecord[] = [
    {
      id: 'SRV001',
      date: new Date(2024, 2, 15),
      type: 'maintenance',
      status: 'completed',
      vehicle: {
        make: 'Ferrari',
        model: '488 Pista',
        year: 2024,
        vin: 'ZFF87NXX000123456',
        registration: 'GP-PISTA-24',
      },
      description: 'Annual maintenance service with brake system upgrade',
      technician: 'James Mitchell',
      cost: 185000,
      parts: [
        {
          partNumber: 'F488-BP-001',
          name: 'Carbon Ceramic Brake System',
          quantity: 1,
          unitPrice: 165000,
        },
        {
          partNumber: 'F488-OIL-24',
          name: 'Shell Helix Ultra Racing Oil',
          quantity: 2,
          unitPrice: 10000,
        },
      ],
      documents: [
        { type: 'Service Report', url: '/documents/srv-001.pdf' },
        { type: 'Parts Certificate', url: '/documents/cert-001.pdf' },
        { type: 'Warranty Update', url: '/documents/warranty-001.pdf' },
      ],
      notes: 'Complete brake system upgrade performed. All systems functioning optimally.',
      mileage: 5000,
      nextService: new Date(2024, 8, 15),
    },
    {
      id: 'SRV002',
      date: new Date(2024, 2, 16),
      type: 'repair',
      status: 'in_progress',
      vehicle: {
        make: 'McLaren',
        model: '765LT',
        year: 2023,
        vin: 'SBM13YAA4NCW12345',
        registration: 'GP-765LT-23',
      },
      description: 'ECU diagnostics and software update',
      technician: 'Sarah Williams',
      cost: 45000,
      parts: [
        {
          partNumber: 'MCL-765-ECU-SW',
          name: 'ECU Software Update Package',
          quantity: 1,
          unitPrice: 45000,
        },
      ],
      documents: [
        { type: 'Diagnostic Report', url: '/documents/diag-002.pdf' },
        { type: 'Software Specs', url: '/documents/spec-002.pdf' },
      ],
      notes: 'Performance optimization in progress. Requires extensive testing.',
      mileage: 8500,
      nextService: new Date(2024, 8, 16),
    },
    {
      id: 'SRV003',
      date: new Date(2024, 2, 20),
      type: 'upgrade',
      status: 'scheduled',
      vehicle: {
        make: 'Porsche',
        model: '911 GT3 RS',
        year: 2024,
        vin: 'WP0ZZZ99ZTS392045',
        registration: 'GP-GT3RS-24',
      },
      description: 'Performance package installation',
      technician: 'Michael van der Merwe',
      cost: 450000,
      parts: [
        {
          partNumber: 'GT3-AERO-001',
          name: 'Aerodynamic Package',
          quantity: 1,
          unitPrice: 385000,
        },
        {
          partNumber: 'GT3-SUSP-001',
          name: 'Sport Suspension Kit',
          quantity: 1,
          unitPrice: 65000,
        },
      ],
      documents: [
        { type: 'Installation Guide', url: '/documents/install-003.pdf' },
        { type: 'Performance Specs', url: '/documents/specs-003.pdf' },
      ],
      notes: 'Full performance package installation scheduled. Track testing required post-installation.',
      mileage: 2500,
      nextService: new Date(2024, 8, 20),
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'warning';
      case 'scheduled':
        return 'info';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'maintenance':
        return 'primary';
      case 'repair':
        return 'error';
      case 'inspection':
        return 'info';
      case 'upgrade':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    return `R ${amount.toLocaleString()}`;
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const handleViewRecord = (record: ServiceRecord) => {
    setSelectedRecord(record);
    setOpenDialog(true);
  };

  const handleFilterClick = () => {
    setOpenFilterDialog(true);
  };

  const handleFilterApply = () => {
    // Here you would typically filter the service records based on the selected filters
    setOpenFilterDialog(false);
  };

  const handleExportClick = () => {
    // Create CSV content
    const headers = ['ID', 'Date', 'Type', 'Status', 'Vehicle', 'Description', 'Technician', 'Cost'];
    const csvContent = [
      headers.join(','),
      ...serviceRecords.map(record => [
        record.id,
        formatDate(record.date),
        record.type,
        record.status,
        `${record.vehicle.make} ${record.vehicle.model} ${record.vehicle.year}`,
        record.description,
        record.technician,
        record.cost
      ].join(','))
    ].join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `service_history_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleNewServiceClick = () => {
    setOpenNewServiceDialog(true);
  };

  const handleNewServiceSubmit = () => {
    // Here you would typically make an API call to create the service record
    setOpenNewServiceDialog(false);
    // Reset form
    setNewService({
      type: 'maintenance',
      vehicle: {
        make: '',
        model: '',
        year: new Date().getFullYear(),
        vin: '',
        registration: '',
      },
      description: '',
      technician: '',
      mileage: 0,
      parts: [],
      notes: '',
    });
  };

  const handleAddPart = () => {
    setNewService({
      ...newService,
      parts: [
        ...newService.parts,
        {
          partNumber: '',
          name: '',
          quantity: 1,
          unitPrice: 0,
        },
      ],
    });
  };

  const handleRemovePart = (index: number) => {
    setNewService({
      ...newService,
      parts: newService.parts.filter((_, i) => i !== index),
    });
  };

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Service History
        </Typography>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card sx={{ minWidth: 240 }}>
              <CardContent>
                <Typography variant="h6" color="text.secondary" noWrap>
                  Active Services
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    wordBreak: 'break-word'
                  }}
                >
                  {serviceRecords.filter(record => record.status === 'in_progress').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  In progress
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ minWidth: 240 }}>
              <CardContent>
                <Typography variant="h6" color="text.secondary" noWrap>
                  Scheduled Services
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    wordBreak: 'break-word'
                  }}
                >
                  {serviceRecords.filter(record => record.status === 'scheduled').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Upcoming
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ minWidth: 240 }}>
              <CardContent>
                <Typography variant="h6" color="text.secondary" noWrap>
                  Total Revenue
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    wordBreak: 'break-word'
                  }}
                >
                  {formatCurrency(serviceRecords.reduce((sum, record) => sum + record.cost, 0))}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  All services
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ minWidth: 240 }}>
              <CardContent>
                <Typography variant="h6" color="text.secondary" noWrap>
                  Completed Services
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    wordBreak: 'break-word'
                  }}
                >
                  {serviceRecords.filter(record => record.status === 'completed').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Successfully delivered
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search service records..."
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<FilterIcon />}
                  onClick={handleFilterClick}
                >
                  Filter
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportClick}
                >
                  Export
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleNewServiceClick}
                >
                  New Service Record
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Service Details</TableCell>
                <TableCell>Vehicle</TableCell>
                <TableCell>Type & Status</TableCell>
                <TableCell>Technician</TableCell>
                <TableCell>Cost</TableCell>
                <TableCell>Documents</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {serviceRecords.map((record) => (
                <TableRow key={record.id}>
                  <TableCell>
                    <Typography variant="subtitle2">{record.id}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(record.date)}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      Next Service: {formatDate(record.nextService)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {record.vehicle.make} {record.vehicle.model}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {record.vehicle.year}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      VIN: {record.vehicle.vin}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack spacing={1}>
                      <Chip
                        label={record.type.charAt(0).toUpperCase() + record.type.slice(1)}
                        color={getTypeColor(record.type)}
                        size="small"
                      />
                      <Chip
                        label={record.status.charAt(0).toUpperCase() + record.status.slice(1)}
                        color={getStatusColor(record.status)}
                        size="small"
                      />
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {record.technician}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Mileage: {record.mileage.toLocaleString()} km
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(record.cost)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      {record.documents.map((doc, index) => (
                        <IconButton
                          key={index}
                          size="small"
                          color="primary"
                          onClick={() => window.open(doc.url, '_blank')}
                        >
                          <DescriptionIcon />
                        </IconButton>
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleViewRecord(record)}
                    >
                      <TimelineIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Service Record Details
          </DialogTitle>
          <DialogContent>
            {selectedRecord && (
              <Box sx={{ mt: 2 }}>
                <Tabs
                  value={currentTab}
                  onChange={(_, newValue) => setCurrentTab(newValue)}
                  sx={{ mb: 2 }}
                >
                  <Tab label="Overview" />
                  <Tab label="Parts Used" />
                  <Tab label="Documents" />
                </Tabs>

                {currentTab === 0 && (
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>
                        Service Information
                      </Typography>
                      <Typography variant="body2">
                        Service ID: {selectedRecord.id}
                      </Typography>
                      <Typography variant="body2">
                        Date: {formatDate(selectedRecord.date)}
                      </Typography>
                      <Typography variant="body2">
                        Type: {selectedRecord.type.charAt(0).toUpperCase() + selectedRecord.type.slice(1)}
                      </Typography>
                      <Typography variant="body2">
                        Status: {selectedRecord.status.charAt(0).toUpperCase() + selectedRecord.status.slice(1)}
                      </Typography>
                      <Typography variant="body2">
                        Technician: {selectedRecord.technician}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>
                        Vehicle Details
                      </Typography>
                      <Typography variant="body2">
                        {selectedRecord.vehicle.make} {selectedRecord.vehicle.model} {selectedRecord.vehicle.year}
                      </Typography>
                      <Typography variant="body2">
                        VIN: {selectedRecord.vehicle.vin}
                      </Typography>
                      <Typography variant="body2">
                        Registration: {selectedRecord.vehicle.registration}
                      </Typography>
                      <Typography variant="body2">
                        Mileage: {selectedRecord.mileage.toLocaleString()} km
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" gutterBottom>
                        Service Description
                      </Typography>
                      <Typography variant="body2">
                        {selectedRecord.description}
                      </Typography>
                      <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>
                        Notes
                      </Typography>
                      <Typography variant="body2">
                        {selectedRecord.notes}
                      </Typography>
                    </Grid>
                  </Grid>
                )}

                {currentTab === 1 && (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Part Number</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell align="right">Quantity</TableCell>
                          <TableCell align="right">Unit Price</TableCell>
                          <TableCell align="right">Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedRecord.parts.map((part) => (
                          <TableRow key={part.partNumber}>
                            <TableCell>{part.partNumber}</TableCell>
                            <TableCell>{part.name}</TableCell>
                            <TableCell align="right">{part.quantity}</TableCell>
                            <TableCell align="right">{formatCurrency(part.unitPrice)}</TableCell>
                            <TableCell align="right">{formatCurrency(part.quantity * part.unitPrice)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell colSpan={4} align="right">
                            <Typography variant="subtitle2">Total Cost:</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="subtitle2">{formatCurrency(selectedRecord.cost)}</Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {currentTab === 2 && (
                  <Grid container spacing={2}>
                    {selectedRecord.documents.map((doc, index) => (
                      <Grid item xs={12} md={4} key={index}>
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<DescriptionIcon />}
                          onClick={() => window.open(doc.url, '_blank')}
                        >
                          {doc.type}
                        </Button>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Filter Dialog */}
        <Dialog
          open={openFilterDialog}
          onClose={() => setOpenFilterDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Filter Service Records</DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <Typography variant="subtitle2" gutterBottom>
                    Service Type
                  </Typography>
                  <FormGroup>
                    {['maintenance', 'repair', 'inspection', 'upgrade'].map((type) => (
                      <FormControlLabel
                        key={type}
                        control={
                          <Checkbox
                            checked={filters.type.includes(type)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFilters({
                                  ...filters,
                                  type: [...filters.type, type],
                                });
                              } else {
                                setFilters({
                                  ...filters,
                                  type: filters.type.filter((t) => t !== type),
                                });
                              }
                            }}
                          />
                        }
                        label={type.charAt(0).toUpperCase() + type.slice(1)}
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <Typography variant="subtitle2" gutterBottom>
                    Status
                  </Typography>
                  <FormGroup>
                    {['scheduled', 'in_progress', 'completed', 'cancelled'].map((status) => (
                      <FormControlLabel
                        key={status}
                        control={
                          <Checkbox
                            checked={filters.status.includes(status)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFilters({
                                  ...filters,
                                  status: [...filters.status, status],
                                });
                              } else {
                                setFilters({
                                  ...filters,
                                  status: filters.status.filter((s) => s !== status),
                                });
                              }
                            }}
                          />
                        }
                        label={status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={filters.dateRange.start}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      dateRange: { ...filters.dateRange, start: e.target.value },
                    })
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={filters.dateRange.end}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      dateRange: { ...filters.dateRange, end: e.target.value },
                    })
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Technician"
                  value={filters.technician}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      technician: e.target.value,
                    })
                  }
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenFilterDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleFilterApply}>
              Apply Filters
            </Button>
          </DialogActions>
        </Dialog>

        {/* New Service Dialog */}
        <Dialog
          open={openNewServiceDialog}
          onClose={() => setOpenNewServiceDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Create New Service Record</DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Service Type</InputLabel>
                  <Select
                    value={newService.type}
                    label="Service Type"
                    onChange={(e) => setNewService({ ...newService, type: e.target.value as any })}
                  >
                    <MenuItem value="maintenance">Maintenance</MenuItem>
                    <MenuItem value="repair">Repair</MenuItem>
                    <MenuItem value="inspection">Inspection</MenuItem>
                    <MenuItem value="upgrade">Upgrade</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Vehicle Make"
                  value={newService.vehicle.make}
                  onChange={(e) => setNewService({
                    ...newService,
                    vehicle: { ...newService.vehicle, make: e.target.value }
                  })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Vehicle Model"
                  value={newService.vehicle.model}
                  onChange={(e) => setNewService({
                    ...newService,
                    vehicle: { ...newService.vehicle, model: e.target.value }
                  })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Year"
                  type="number"
                  value={newService.vehicle.year}
                  onChange={(e) => setNewService({
                    ...newService,
                    vehicle: { ...newService.vehicle, year: parseInt(e.target.value) || new Date().getFullYear() }
                  })}
                  InputProps={{ inputProps: { min: 1900, max: new Date().getFullYear() + 1 } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="VIN"
                  value={newService.vehicle.vin}
                  onChange={(e) => setNewService({
                    ...newService,
                    vehicle: { ...newService.vehicle, vin: e.target.value }
                  })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Registration"
                  value={newService.vehicle.registration}
                  onChange={(e) => setNewService({
                    ...newService,
                    vehicle: { ...newService.vehicle, registration: e.target.value }
                  })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={newService.description}
                  onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Technician"
                  value={newService.technician}
                  onChange={(e) => setNewService({ ...newService, technician: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Mileage"
                  type="number"
                  value={newService.mileage}
                  onChange={(e) => setNewService({ ...newService, mileage: parseInt(e.target.value) || 0 })}
                  InputProps={{ inputProps: { min: 0 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1">Parts</Typography>
                  <Button startIcon={<AddIcon />} onClick={handleAddPart}>
                    Add Part
                  </Button>
                </Box>
                {newService.parts.map((part, index) => (
                  <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Part Number"
                        value={part.partNumber}
                        onChange={(e) => {
                          const updatedParts = [...newService.parts];
                          updatedParts[index] = { ...part, partNumber: e.target.value };
                          setNewService({ ...newService, parts: updatedParts });
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Part Name"
                        value={part.name}
                        onChange={(e) => {
                          const updatedParts = [...newService.parts];
                          updatedParts[index] = { ...part, name: e.target.value };
                          setNewService({ ...newService, parts: updatedParts });
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        label="Quantity"
                        type="number"
                        value={part.quantity}
                        onChange={(e) => {
                          const updatedParts = [...newService.parts];
                          updatedParts[index] = { ...part, quantity: parseInt(e.target.value) || 1 };
                          setNewService({ ...newService, parts: updatedParts });
                        }}
                        InputProps={{ inputProps: { min: 1 } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Unit Price"
                        type="number"
                        value={part.unitPrice}
                        onChange={(e) => {
                          const updatedParts = [...newService.parts];
                          updatedParts[index] = { ...part, unitPrice: parseFloat(e.target.value) || 0 };
                          setNewService({ ...newService, parts: updatedParts });
                        }}
                        InputProps={{ inputProps: { min: 0 } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={1}>
                      <IconButton onClick={() => handleRemovePart(index)}>
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={2}
                  value={newService.notes}
                  onChange={(e) => setNewService({ ...newService, notes: e.target.value })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenNewServiceDialog(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleNewServiceSubmit}
              disabled={!newService.vehicle.make || !newService.vehicle.model || !newService.description || !newService.technician}
            >
              Create Service Record
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  );
}; 